
import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';


// The client is built on first use, never at import time.
//
// `next build` evaluates every module in an API route's graph while collecting
// page data, and this one is reachable from /api/mcp via lib/supabaseService.
// Constructing a BROWSER client eagerly therefore ran during the build, on a
// machine that has no NEXT_PUBLIC_SUPABASE_* in its environment, and
// @supabase/ssr threw: "Failed to collect page data for /api/mcp". It only
// looked fine locally and on Vercel because both supply the vars. CI cannot,
// and Dependabot pull requests cannot be given repo secrets at all, so gating
// this on a secret would have left every Dependabot PR red.
//
// Deferring also puts the failure where it belongs: a missing key now surfaces
// at the first real query, with a message, instead of at module load.
let client: SupabaseClient | null = null;

function getClient(): SupabaseClient {
    if (client) return client;

    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase URL or Anon Key is missing. Check your .env.local file.');
    }

    client = createBrowserClient(supabaseUrl, supabaseAnonKey);
    return client;
}

// A Proxy keeps the export shape identical for all nine call sites, so nothing
// downstream has to know the client is lazy. Methods are bound to the real
// client, never to the proxy, because supabase-js reads its own internal state
// off `this`.
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
    get(_target, prop) {
        const instance = getClient() as unknown as Record<string | symbol, unknown>;
        const value = instance[prop];
        return typeof value === 'function'
            ? (value as (...args: unknown[]) => unknown).bind(instance)
            : value;
    },
});
